import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetIntellipayWidget, PostPayment } from "../services/PaymentService";
import { useAxiosInstance } from "../services/Interceptor";
import { RegisterAthlete } from "../services/EventsService";
import { useSelector } from "react-redux";
import { registerCoach } from "../services/CoachService";
import {
  registerAthlete,
  registerAthleteWithEventId,
} from "../services/AthleteService";
import {
  GetAdminPrice,
  GetEventCombinePrice,
  SaveAppliedCouponCode,
} from "../services/EventsService";
import { useTrackEvent } from "../hooks/useTrackEvent";

const INTELLIPAY_POLL_INTERVAL_MS = 500;
const INTELLIPAY_POLL_MAX_ATTEMPTS = 20; // 10-second ceiling
const INTELLIPAY_READY_FALLBACK_DELAY_MS = 50;

export default function PayWidget(props) {
  const { onIntellipayReady } = props;
  const apiInstance = useAxiosInstance();
  const trackEvent = useTrackEvent();
  const trackEventRef = useRef(trackEvent);
  const accounts = useSelector((state) => state.account.register);
  const { subscriptionType, subscriptionPrice } = useSelector(
    (state) => state.account,
  );
  const navigate = useNavigate();

  const subscriptionTypeRef = useRef(subscriptionType);
  const subscriptionPriceRef = useRef(subscriptionPrice);
  const intervalRef = useRef(null);
  const readyFallbackTimeoutRef = useRef(null);
  const isReInitializationRef = useRef(false);

  useEffect(() => {
    subscriptionTypeRef.current = subscriptionType;
    subscriptionPriceRef.current = subscriptionPrice;
  }, [subscriptionType, subscriptionPrice]);

  useEffect(() => {
    trackEventRef.current = trackEvent;
  }, [trackEvent]);

  useEffect(() => {
    // Track if this is a re-initialization (widget already exists)
    isReInitializationRef.current = !!(
      window.intellipay && window.__intellipayWidgetLoaded
    );

    getIntellipayWidget();

    // Cleanup function to clear intervals/timeouts only
    // Don't reset the widget flag since the script persists across route changes
    return () => {
      const currentInterval = intervalRef.current;
      if (currentInterval) {
        clearInterval(currentInterval);
        intervalRef.current = null;
      }
      const currentReadyFallback = readyFallbackTimeoutRef.current;
      if (currentReadyFallback) {
        clearTimeout(currentReadyFallback);
        readyFallbackTimeoutRef.current = null;
      }
      // Don't reset window.__intellipayWidgetLoaded here
      // The script stays in the DOM during React Router navigation
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetIntellipayWidget = () => {
    // Only reset after non-approval, not on unmount
    window.__intellipayWidgetLoaded = false;
  };

  const handleFixAmount = (inputValue) => {
    if (typeof inputValue !== "string") {
      if (typeof inputValue === "number") {
        return inputValue.toFixed(2);
      } else {
        console.warn("Invalid amount format. Returning default value.");
        return "0.00";
      }
    }

    if (
      window.intellipay &&
      typeof window.intellipay.fixAmount === "function"
    ) {
      let tempamount = window.intellipay.fixAmount(inputValue);
      return tempamount;
    } else {
      console.warn("fixAmount is missing! Using fallback.");
      return parseFloat(inputValue.replace(/[^0-9.]/g, "") || "0").toFixed(2);
    }
  };
  const accountsRef = useRef(accounts);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  const getAthletePricing = async () => {
    const res = await GetAdminPrice(apiInstance);
    const athlete = res?.data?.find(
      (item) => item.role.toLowerCase() === "athlete",
    );
    return athlete?.pricingOptions || {};
  };

  const getCombinePositionDrillPrice = async (response, props) => {
    const combinePriceRes = await GetEventCombinePrice(
      apiInstance,
      props.eventId,
    );

    let combineDrillPayload = [];

    if (props.subscriptionType === "combinePosition") {
      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes.data.combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });

      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes.data.positionDrillPrice),
        isCombine: false,
        isPositionDrill: true,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    } else {
      combineDrillPayload.push({
        userId: props.userId,
        amount: handleFixAmount(combinePriceRes.data.combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    return combineDrillPayload;
  };

  const postAppliedCouponCode = async (code) => {
    await SaveAppliedCouponCode(apiInstance, code);
    localStorage.removeItem("appliedCouponCode");
  };

  const postPaymentSuccess = async (response, props) => {
    let combineDrillPayload = [];
    if (
      props.subscriptionType === "combine" ||
      props.subscriptionType === "combinePosition"
    ) {
      combineDrillPayload = await getCombinePositionDrillPrice(response, props);
    }

    const currentSubscriptionType = subscriptionTypeRef.current;

    const isCombine = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("combine")
      : currentSubscriptionType === "combine";

    const isMonthlySubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("monthly")
      : currentSubscriptionType === "monthly";

    const isAnnualSubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("annual")
      : currentSubscriptionType === "annual";

    const isCombinePositionDrill = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("positionDrill")
      : currentSubscriptionType === "positionDrill";

    const isSubscription = Array.isArray(currentSubscriptionType)
      ? currentSubscriptionType.includes("subscription")
      : currentSubscriptionType === "subscription";

    let combinePrice = 0;
    let positionDrillPrice = 0;
    let athletePrices = {};
    let paymentPayloads = [];

    if (isCombine) {
      const combinePriceResponse = await GetEventCombinePrice(
        apiInstance,
        props.eventId,
      );
      combinePrice = combinePriceResponse?.data.combinePrice || 0;
      positionDrillPrice = combinePriceResponse?.data.positionDrillPrice || 0;

      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(combinePrice),
        isCombine: true,
        isPositionDrill: false,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    athletePrices = await getAthletePricing();

    if (isCombinePositionDrill) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(positionDrillPrice),
        isCombine: false,
        isPositionDrill: true,
        isAnnualSubscription: false,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    if (isSubscription) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(athletePrices.annualPrice ?? 0),
        isCombine: false,
        isPositionDrill: false,
        isAnnualSubscription: true,
        isMonthlySubscription: false,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    if (isAnnualSubscription || isMonthlySubscription) {
      paymentPayloads.push({
        userId: props.userId,
        amount: handleFixAmount(subscriptionPriceRef.current),
        isCombine: false,
        isPositionDrill: false,
        isAnnualSubscription,
        isMonthlySubscription,
        isCash: false,
        eventId: props.eventId,
        transactionId: response?.receiptelements?.transid || "",
        intellipayToken: String(response?.custid || ""),
      });
    }

    try {
      const currentAccounts = accountsRef.current;
      if (currentAccounts.length > 0) {
        const user = currentAccounts[0];
        const normalizedRole = user?.role?.toLowerCase?.();

        if (normalizedRole === "highschool") {
          if (!user.registered) {
            const reg = await registerCoach(apiInstance, user);
            if (reg.status !== 200) {
              throw new Error("High school registration failed.");
            }
          }

          const couponCode = localStorage.getItem("appliedCouponCode");
          if (couponCode) {
            await postAppliedCouponCode(couponCode);
          }
          trackEventRef.current("Registration5_RegistrationCompleted", {
            event_id: props.eventId,
            role: "highschool",
            portal_type: "highschool",
          });
          toast.success("High school registration successful!", {
            position: "top-right",
          });
          localStorage.removeItem("appliedCouponCode");
          localStorage.removeItem("subscriptionDetails");
          localStorage.removeItem("registeredAccount");
          navigate("/register/registrationsuccess");
          return;
        }

        if (user.registered) {
          for (const payload of paymentPayloads) {
            const storedData = JSON.parse(
              localStorage.getItem("registeredAccount") || "{}",
            );
            const userReg = user.length ? user[0] : storedData;
            const updatedPaymentPayload = {
              ...payload,
              userId: userReg.userId,
              eventId: 0,
            };
            const paid = await PostPayment(apiInstance, updatedPaymentPayload);
            if (paid.status === 200) {
              const couponCode = localStorage.getItem("appliedCouponCode");
              if (couponCode) {
                await postAppliedCouponCode(couponCode);
              }
              trackEventRef.current("Registration5_RegistrationCompleted", {
                event_id: props.eventId,
                role: "athlete_existing",
                portal_type: "highschool",
              });
              toast.success(
                "Athlete payment successful.Verify Your Email to Get Started!",
                {
                  position: "top-right",
                },
              );
              localStorage.removeItem("appliedCouponCode");
              localStorage.removeItem("registeredAccount");
              window.location.href = "/athlete/dashboard";
            } else {
              toast.error("Your Payment is failed.", { position: "top-right" });
            }
          }
        } else if (user.role === "university") {
          const reg = await registerCoach(apiInstance, user);
          if (reg.status === 200) {
            const newUserId = reg.data.id;
            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: 0,
              };

              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );
              if (paid.status === 200) {
                trackEventRef.current("Registration5_RegistrationCompleted", {
                  event_id: props.eventId,
                  role: "university",
                  portal_type: "highschool",
                });
                toast.success("Coach registration & payment successful!", {
                  position: "top-right",
                });
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                navigate("/register/registrationsuccess");
              } else {
                toast.error("Payment after coach registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Coach registration failed.", {
              position: "top-right",
            });
          }
        } else if (user.role === "athlete") {
          const reg = await registerAthlete(apiInstance, "0", user);
          if (reg.status === 200) {
            const newUserId = reg.data.id;
            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: 0,
              };
              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );
              if (paid.status === 200) {
                trackEventRef.current("Registration5_RegistrationCompleted", {
                  event_id: props.eventId,
                  role: "athlete",
                  portal_type: "highschool",
                });
                toast.success("Athlete registration & payment successful!", {
                  position: "top-right",
                });
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                localStorage.removeItem("appliedCouponCode");
                localStorage.removeItem("subscriptionDetails");
                localStorage.removeItem("registeredAccount");
                navigate("/register/registrationsuccess");
              } else {
                toast.error("Payment after athlete registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Athlete registration failed.", {
              position: "top-right",
            });
          }
        } else if (user.role === "spotcombine") {
          const spotcombineRegRes = await registerAthleteWithEventId(
            apiInstance,
            user,
            props.eventId,
          );
          if (spotcombineRegRes.status === 200) {
            const newUserId = spotcombineRegRes.data.id;
            let allSuccessful = true;

            for (const payload of paymentPayloads) {
              const updatedPaymentPayload = {
                ...payload,
                userId: newUserId,
                eventId: props.eventId,
              };

              const paid = await PostPayment(
                apiInstance,
                updatedPaymentPayload,
              );

              if (paid.status !== 200) {
                allSuccessful = false;
                toast.error("Payment after spot registration failed.", {
                  position: "top-right",
                });
                break;
              }
            }

            if (allSuccessful) {
              const couponCode = localStorage.getItem("appliedCouponCode");
              if (couponCode) {
                await postAppliedCouponCode(couponCode);
              }
              trackEventRef.current("Registration5_RegistrationCompleted", {
                event_id: props.eventId,
                role: "spotcombine",
                portal_type: "highschool",
              });
              toast.success("Athlete spot registration & payment successful!", {
                position: "top-right",
              });
              localStorage.removeItem("appliedCouponCode");
              localStorage.removeItem("subscriptionDetails");
              localStorage.removeItem("registeredAccount");
              navigate("/register/registrationsuccess");
            }
          } else {
            toast.error("Athlete spot registration failed.", {
              position: "top-right",
            });
          }
        } else {
          const regRes = await RegisterAthlete(
            apiInstance,
            user.eventId,
            user.userId,
            props.agreedToWaiver,
            props.tShirtSize,
            props.offerAcceptanceStatus,
            props.offerAcceptanceSchool,
          );
          if (regRes.status === 200) {
            for (const payload of paymentPayloads) {
              const paidRes = await PostPayment(apiInstance, payload);
              if (paidRes.status === 200) {
                const couponCode = localStorage.getItem("appliedCouponCode");
                if (couponCode) {
                  await postAppliedCouponCode(couponCode);
                }
                trackEventRef.current("Registration5_RegistrationCompleted", {
                  event_id: props.eventId,
                  role: "event_athlete",
                  portal_type: "highschool",
                });
                toast.success(
                  "Athlete event registration and payment is successful",
                  {
                    position: "top-right",
                  },
                );
                localStorage.removeItem("appliedCouponCode");
                localStorage.removeItem("subscriptionDetails");
                localStorage.removeItem("registeredAccount");
                props.onRegistrationSuccess();
              } else {
                toast.error("Payment after event registration failed.", {
                  position: "top-right",
                });
              }
            }
          } else {
            toast.error("Your event registration failed", {
              position: "top-right",
            });
          }
        }
      } else {
        const regRes = await RegisterAthlete(
          apiInstance,
          props.eventId,
          props.userId,
          props.agreedToWaiver,
          props.tShirtSize,
          props.offerAcceptanceStatus,
          props.offerAcceptanceSchool,
        );
        if (regRes.status === 200) {
          let allSuccessful = true;

          for (const combineDrills of combineDrillPayload) {
            const paidRes = await PostPayment(apiInstance, combineDrills);
            if (paidRes.status !== 200) {
              allSuccessful = false;
              toast.error("Payment after event registration failed.", {
                position: "top-right",
              });
              break;
            }
          }

          if (allSuccessful) {
            const couponCode = localStorage.getItem("appliedCouponCode");
            if (couponCode) {
              await postAppliedCouponCode(couponCode);
            }
            trackEventRef.current("Registration5_RegistrationCompleted", {
              event_id: props.eventId,
              role: "event_combine",
              portal_type: "highschool",
            });
            toast.success(
              "Athlete event combine registration and payment is successful",
              {
                position: "top-right",
              },
            );
            localStorage.removeItem("appliedCouponCode");
            localStorage.removeItem("registeredAccount");
            props.onRegistrationSuccess();
          }
        } else {
          toast.error("Your event registration failed", {
            position: "top-right",
          });
        }
      }
    } catch (error) {
      toast.error("Payment processing failed - " + error.error, {
        position: "top-right",
      });
    }
  };

  const initializeAlreadyLoadedWidget = () => {
    // ✅ hide Customer ID
    if (typeof window.intellipay.disable === "function") {
      window.intellipay.disable("account");
    }
    // Re-register the callbacks first, then initialize
    window.intellipay.runOnApproval((response) =>
      postPaymentSuccess(response, props),
    );
    window.intellipay.runOnNonApproval((res) => {
      console.error("Transaction not approved!", res);
      trackEventRef.current("Registration4_5_PaymentFailed", {
        event_id: props.eventId,
        reason: res?.message ?? "non-approval",
        portal_type: "highschool",
      });
      resetIntellipayWidget();
    });

    window.intellipay.runOnClose(() => {
      // Don't trigger onClose callback when user simply closes the widget
      // The callback should only be triggered after successful payment
    });

    // Initialize AFTER setting up callbacks
    try {
      window.intellipay.initialize();

      const finishReadyHandling = () => {
        readyFallbackTimeoutRef.current = null;
        if (typeof window.intellipay.updatefields === "function") {
          window.intellipay.updatefields();
        }

        // Also try calling readyHandler to ensure it's fully ready
        if (typeof window.intellipay.readyHandler === "function") {
          window.intellipay.readyHandler();
        }
      };

      // initialize() renders synchronously in practice; the fallback delay
      // only guards against a slower DOM update on the widget's side.
      readyFallbackTimeoutRef.current = setTimeout(
        finishReadyHandling,
        INTELLIPAY_READY_FALLBACK_DELAY_MS,
      );
    } catch (e) {
      console.warn("Error during initialize:", e);
    }

    window.__intellipayWidgetLoaded = true;
    onIntellipayReady?.(true);
  };

  const initializeNewlyLoadedWidget = () => {
    if (typeof window.intellipay.disable === "function") {
      window.intellipay.disable("account");
    }

    window.intellipay.initialize();
    window.intellipay.runOnApproval((response) =>
      postPaymentSuccess(response, props),
    );
    window.intellipay.runOnNonApproval((res) => {
      console.error("Transaction not approved!", res);
      resetIntellipayWidget();
    });

    window.intellipay.runOnClose(() => {
      // Don't trigger onClose callback when user simply closes the widget
      // The callback should only be triggered after successful payment
    });

    window.__intellipayWidgetLoaded = true;
    trackEventRef.current("Registration4_OpenedPaymentModal", {
      event_id: props.eventId,
      portal_type: "highschool",
    });
    onIntellipayReady?.(true);
  };

  const pollForIntellipay = () => {
    let attempts = 0;

    intervalRef.current = setInterval(() => {
      attempts++;

      if (window.intellipay) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        initializeNewlyLoadedWidget();
      } else if (attempts >= INTELLIPAY_POLL_MAX_ATTEMPTS) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        trackEventRef.current("Registration4_PaymentWidgetLoadFailed", {
          event_id: props.eventId,
          portal_type: "highschool",
        });
        console.error(
          "Intellipay widget failed to load after maximum attempts",
        );
      }
    }, INTELLIPAY_POLL_INTERVAL_MS);
  };

  const getIntellipayWidget = async () => {
    // Check if window.intellipay exists (script loaded)
    if (window.intellipay) {
      initializeAlreadyLoadedWidget();
      return;
    }

    // Check if script already exists in DOM but not fully loaded yet
    const existingScript = document.querySelector("script[src*='intellipay']");
    const existingContainer =
      document.querySelector("#intellipay-container") ||
      document.querySelector("[id*='intellipay']");

    if (existingScript || existingContainer) {
      // The script is already in flight; there's no load event to attach to
      // retroactively, so fall back to polling until it exposes window.intellipay.
      pollForIntellipay();
      return;
    }

    // Load the widget for the first time
    const response = await GetIntellipayWidget(apiInstance);
    if (!response.success) {
      return;
    }

    const range = document.createRange();
    const fragment = range.createContextualFragment(response.data);
    const injectedScript = fragment.querySelector("script[src]");

    // Only append if not already in the DOM
    if (document.querySelector("script[src*='intellipay']")) {
      return;
    }

    if (injectedScript) {
      // Prefer the script's own load event over polling: it fires as soon as
      // the widget script has executed, instead of waiting for the next tick.
      injectedScript.addEventListener(
        "load",
        () => {
          if (window.intellipay) {
            initializeNewlyLoadedWidget();
          } else {
            // Loaded but hasn't attached window.intellipay yet; fall back to a
            // bounded poll rather than assuming failure.
            pollForIntellipay();
          }
        },
        { once: true },
      );
      injectedScript.addEventListener(
        "error",
        () => {
          trackEventRef.current("Registration4_PaymentWidgetLoadFailed", {
            event_id: props.eventId,
            portal_type: "highschool",
          });
          console.error("Intellipay widget script failed to load.");
        },
        { once: true },
      );
    }

    document.documentElement.appendChild(fragment);

    if (!injectedScript) {
      // No <script src> found in the returned markup; fall back to polling.
      pollForIntellipay();
    }
  };

  useEffect(() => {
    // Manually handle submit button clicks ONLY when widget is re-initialized
    const handleSubmitClick = (e) => {
      if (e.target.dataset.ipayname === "submit") {
        // ONLY manually intervene if this was a re-initialization
        if (isReInitializationRef.current && window.intellipay) {
          e.preventDefault();
          e.stopPropagation();

          if (typeof window.intellipay.disable === "function") {
            window.intellipay.disable("account");
          }

          // Update fields right before opening to ensure current values are used
          if (typeof window.intellipay.updatefields === "function") {
            window.intellipay.updatefields();
          }

          // Set values explicitly using setvalue if available
          const amountField = document.querySelector(
            '.ipayfield[data-ipayname="amount"]',
          );
          const emailField = document.querySelector(
            '.ipayfield[data-ipayname="email"]',
          );

          if (typeof window.intellipay.setvalue === "function") {
            if (amountField) {
              window.intellipay.setvalue("amount", amountField.value);
            }
            if (emailField) {
              window.intellipay.setvalue("email", emailField.value);
            }
          }

          // Try to trigger the form submission properly
          // First try onSubmitInternal which should process the form values
          if (typeof window.intellipay.onSubmitInternal === "function") {
            window.intellipay.onSubmitInternal(e);
          } else if (typeof window.intellipay.onSubmit === "function") {
            window.intellipay.onSubmit(e);
          } else if (typeof window.intellipay.checkform === "function") {
            const formValid = window.intellipay.checkform();
            if (
              formValid &&
              typeof window.intellipay.openModal === "function"
            ) {
              window.intellipay.openModal();
            }
          } else if (typeof window.intellipay.openModal === "function") {
            window.intellipay.openModal();
          }
        }
      }
    };

    // Use capture phase to intercept before Intellipay's listener
    document.addEventListener("click", handleSubmitClick, true);
    return () => document.removeEventListener("click", handleSubmitClick, true);
  }, []);

  return (
    <div>
      <input
        className="ipayfield"
        data-ipayname="email"
        type="hidden"
        value={props.userEmail || ""}
      />
      <input
        className="ipayfield"
        data-ipayname="amount"
        type="hidden"
        value={props.paymentAmount || 0}
      />
      <ToastContainer />
    </div>
  );
}
